import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { serviceConsumer } from "../../network/ServiceConsumer";
import Loader from "../../Components/Loader/Loader";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import SingleLineTitle from "../../Components/TitleElements/SingleLineTitle";
import ConfirmDeleteModal from "../../Components/Modals/OrganizerModel/ManageFeis/Tabulate/ConfirmDeleteModal";

const SchoolDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolName } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [school, setSchool] = useState(null);
  const [error, setError] = useState("");

  // ✅ states for edit & delete
  const [editMode, setEditMode] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  useEffect(() => {
    fetchSchool();
  }, [id]);

  const fetchSchool = async () => {
    try {
      setLoading(true);
      setError("");
      const url = `${process.env.REACT_APP_BASE_URL}/school/list-dancers/${id}`;
      const res = await serviceConsumer("GET", url);

      if (res?.data) {
        setSchool(res.data);
      } else {
        setError("No school details found");
      }
    } catch (err) {
      setError("Failed to load school details");
      ShowToastMessage("error", "Failed to load school details");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handlers
  const editClickHandler = () => {
    setEditMode(true);
    setEditedName(editedName || schoolName)
  };

  const saveHandler = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_BASE_URL}/school/${id}`;
      const payload = { schoolName: editedName };
      await serviceConsumer("PUT", url, payload);
      ShowToastMessage("success", "School updated successfully");
      setEditMode(false);
    //   fetchSchool();
    } catch (err) {
      ShowToastMessage("error", "Something went wrong while saving");
    } finally {
      setLoading(false);
    }
  };

  const deleteClickHandler = () => {
    setOpenDeleteModal(true);
  };

  const confirmDeleteHandler = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_BASE_URL}/school/${id}`;
      await serviceConsumer("DELETE", url);
      ShowToastMessage("success", "School deleted successfully");
      setOpenDeleteModal(false);
      navigate("/schools");
    } catch (err) {
      ShowToastMessage("error", "Something went wrong while deleting");
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteHandler = () => {
    setOpenDeleteModal(false);
  };

  const backToSchoolsHandler = () => {
    navigate("/schools");
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!school) return <p className="text-center">No data available</p>;

  return (
    <>
      <SingleLineTitle
        titleName={school.schoolName || "School Details"}
        onBackClick={backToSchoolsHandler}
      />

      <div className="db-body">
        {/* Header with edit/delete */}
        <div className="d-flex align-items-center justify-content-between mb-4 pt-3">
          {!editMode ? (
            <h3>{editedName || schoolName}</h3>
          ) : (
            <input
              type="text"
              className="dashboard-input"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              style={{width: "50%"}}
            />
          )}
          <div className="d-flex gap-3">
            {!editMode ? (
              <EditIcon
                style={{ cursor: "pointer", color: "#68ad3f" }}
                titleAccess="Edit School"
                onClick={editClickHandler}
              />
            ) : (
              <SaveIcon
                style={{ cursor: "pointer", color: "#68ad3f" }}
                titleAccess="Save Changes"
                onClick={saveHandler}
              />
            )}
            <DeleteOutlineOutlinedIcon
              style={{ cursor: "pointer", color: "red" }}
              titleAccess="Delete School"
              onClick={deleteClickHandler}
            />
          </div>
        </div>

        {/* Teachers Table */}
        <h4>Teachers</h4>
        <hr />
        <div className="table-responsive">
          <table className="table my_table">
            <tbody>
              {school.teachers?.length > 0 ? (
                school.teachers.map((t, i) => (
                  <tr key={i}>
                    <td>{t.fname || ""}</td>
                    <td>{t.lname || ""}</td>
                    <td>{t.email || "N/A"}</td>
                    <td>{t.country || "N/A"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">
                    No teachers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Dancers List */}
        <h4 className="mt-5">Dancers</h4>
        <hr />
        <div className="table-responsive">
          <table className="table my_table">
            <tbody>
              {school.dancers?.length > 0 ? (
                school.dancers.map((d, i) => (
                  <tr key={i} style={{ fontSize: "14px" }}>
                    <td>
                      <Link className="dancer-link">
                        {`${d.fname} ${d.lname}`}
                      </Link>
                    </td>
                    <td className="text-end">{d.birthday || "N/A"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" className="text-center">
                    No dancers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {openDeleteModal && (
        <ConfirmDeleteModal
          modalState={openDeleteModal}
          confirm={confirmDeleteHandler}
          cancel={cancelDeleteHandler}
        />
      )}
    </>
  );
};

export default SchoolDetails;
